import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  MutableRefObject,
  useEffect,
} from "react";

import { InsideParallaxImage } from "components/Image/Index";
import { Parallax } from "react-scroll-parallax";

import { a, useSpring, to } from "@react-spring/web";
import { useAnimateInTrail } from "hooks/useAnimateInTrail";
import { useGesture } from "react-use-gesture";

import * as fromProjects from "store/Projects";

interface IHeroDimensions {
  width: number;
  height: number;
}

interface IProps {
  wrapperRef: MutableRefObject<HTMLDivElement>;
  animateIn: boolean;
  heroSection: fromProjects.IProjectHeroSection;
}

const DepthParallaxImageComponent: React.FC<IProps> = ({
  wrapperRef,
  animateIn,
  heroSection,
  ...props
}) => {
  const heroProperties: fromProjects.IProjectHeroSectionProperties =
    heroSection.properties as fromProjects.IProjectHeroSectionProperties;
  const [heroDimensions, setHeroDimensions] = useState<IHeroDimensions>({
    width: 1000,
    height: 5000,
  });

  useEffect(() => {
    const updateHeroDimensions = () => {
      const wrapperElement = wrapperRef.current;
      setHeroDimensions({
        width: wrapperElement.getBoundingClientRect().width,
        height: wrapperElement.getBoundingClientRect().height,
      });
    };

    updateHeroDimensions();

    // Setup event listeners on initial mount to keep the  dimensions of the hero updated
    window.addEventListener("resize", updateHeroDimensions);

    return () => {
      window.removeEventListener("resize", updateHeroDimensions);
    };
  }, []);

  const [cursorPosition, setCursorPosition] = useSpring(() => ({ xy: [0, 0] }));
  const bind = useGesture(
    {
      onMove: (state) => handleMove(state), // fires on mouse move over the element
    },
    { domTarget: wrapperRef }
  );
  useEffect(bind, [bind]);

  const handleMove = (state) => {
    const { xy } = state;
    setCursorPosition({ xy });
  };

  const horizontalMediumImageRange = 1;
  const horizontalLargeImageRange = 2;
  const horizontalExtraLargeImageRange = 10;

  const verticalStartOffset = 2.34375; // 2560 => 60
  const verticalSmallImageRange = 0;

  const verticalMediumImageRange = 0.390625; // 2560 => 10
  const verticalLargeImageRange = verticalMediumImageRange * 2; // 2560 => 20
  const verticalExtraLargeImageRange = verticalLargeImageRange * 2; // 2560 => 40

  const onePixel = heroDimensions.width / 2560;
  const [animateInTrail] = useAnimateInTrail(animateIn, 4);

  const firstImageStyles = {
    opacity: animateInTrail[0].active,
    willChange: `opacity`,
  };

  const secondImageStyles = {
    opacity: animateInTrail[1].active,
    transform: to(
      [animateInTrail[1].active, cursorPosition.xy],
      (aIn, xy) =>
        `translateY(calc( (${aIn} * -100px) + 100px + ( ( ( ${heroDimensions.height} - ${xy[1]} ) / ${heroDimensions.height} ) * ${verticalMediumImageRange}vw ) - ${verticalMediumImageRange}vw )) translateX(calc(( ( (( ${heroDimensions.width} / 2 ) - ${xy[0]} ) / ${heroDimensions.width} ) * ${horizontalMediumImageRange}px ) ))`
    ),
    willChange: `opacity, transform`,
  };

  const thirdImageStyles = {
    opacity: animateInTrail[2].active,
    transform: to(
      [animateInTrail[2].active, cursorPosition.xy],
      (aIn, xy) =>
        `translateY(calc( (${aIn} * -70px) + 70px + ( ( ( ${heroDimensions.height} - ${xy[1]} ) / ${heroDimensions.height} ) * ${verticalLargeImageRange}vw ) - ${verticalLargeImageRange}vw )) translateX(calc(( ( (( ${heroDimensions.width} / 2 ) - ${xy[0]} ) / ${heroDimensions.width} ) * ${horizontalLargeImageRange}px ) ))`
    ),
    willChange: `opacity, transform`,
  };

  const fourthImageStyles = {
    opacity: animateInTrail[3].active,
    transform: to(
      [animateInTrail[3].active, cursorPosition.xy],
      (aIn, xy) =>
        `translateY(calc( (${aIn} * -30px) + 30px + ( ( ( ${heroDimensions.height} - ${xy[1]} ) / ${heroDimensions.height} ) * ${verticalExtraLargeImageRange}vw ) - ${verticalExtraLargeImageRange}vw )) translateX(calc(( ( (( ${heroDimensions.width} / 2 ) - ${xy[0]} ) / ${heroDimensions.width} ) * ${horizontalExtraLargeImageRange}px ) ))`
    ),
    willChange: `opacity, transform`,
  };

  return (
    <div className="depthParallaxImageWrapper">
      <Parallax
        y={[
          -110 * onePixel + "px", // 2560 => -110px'
          460 * onePixel + "px", // 2560 => 460px'
        ]}
        tagOuter="figure"
      >
        <a.div className="figureWrapper" style={firstImageStyles}>
          <InsideParallaxImage
            width="100%"
            heightRatio={heroProperties.images[0].heightRatio}
            src={"/assets/images/work/" + heroProperties.images[0].url}
            alt={heroProperties.images[0].alt}
            fit="contain"
            animateImageIn={true}
            animationType={"fadeIn"}
          />
        </a.div>
      </Parallax>
      <Parallax
        y={[
          -110 * onePixel + "px", // 2560 => -110px'
          420 * onePixel + "px", // 2560 => 420px'
        ]}
        tagOuter="figure"
      >
        <a.div className="figureWrapper" style={secondImageStyles}>
          <InsideParallaxImage
            width="100%"
            heightRatio={heroProperties.images[1].heightRatio}
            src={"/assets/images/work/" + heroProperties.images[1].url}
            alt={heroProperties.images[1].alt}
            fit="contain"
            animateImageIn={true}
            animationType={"fadeIn"}
          />
        </a.div>
      </Parallax>
      <Parallax
        y={[
          -110 * onePixel + "px", // 2560 => -110px'
          300 * onePixel + "px", // 2560 => 280px'
        ]}
        tagOuter="figure"
      >
        <a.div className="figureWrapper" style={thirdImageStyles}>
          <InsideParallaxImage
            width="100%"
            heightRatio={heroProperties.images[2].heightRatio}
            src={"/assets/images/work/" + heroProperties.images[2].url}
            alt={heroProperties.images[2].alt}
            fit="contain"
            animateImageIn={true}
            animationType={"fadeIn"}
          />
        </a.div>
      </Parallax>
      <Parallax
        y={[
          -100 * onePixel + "px", // 2560 => -100px'
          40 * onePixel + "px", // 2560 => 40px'
        ]}
        tagOuter="figure"
      >
        <a.div className="figureWrapper" style={fourthImageStyles}>
          <InsideParallaxImage
            width="100%"
            heightRatio={heroProperties.images[3].heightRatio}
            src={"/assets/images/work/" + heroProperties.images[3].url}
            alt={heroProperties.images[3].alt}
            fit="contain"
            animateImageIn={true}
            animationType={"fadeIn"}
          />
        </a.div>
      </Parallax>
    </div>
  );
};

export const DepthParallaxImage = DepthParallaxImageComponent;
